import React, { useState } from 'react';
import { usePortalAuth, portalApi } from './PortalAuthContext';

export function PortalChangePasswordScreen() {
  const { user, refresh, logout } = usePortalAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const propertyName = user?.assignedProperty?.name || 'Availability';

  function validate(): string | null {
    if (!currentPassword) return 'Enter your current password.';
    if (!newPassword) return 'Enter a new password.';
    if (newPassword.length < 8) return 'New password must be at least 8 characters.';
    if (newPassword === currentPassword) return 'New password must be different from current password.';
    if (newPassword !== confirmPassword) return 'New password and confirmation do not match.';
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    setSubmitting(true);
    try {
      await portalApi.changePassword(currentPassword, newPassword);
      await refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setSubmitting(false);
    }
  }

  return (
    <div className="portal-screen portal-screen-centered">
      <div className="portal-card">
        <div className="portal-change-header">
          <div className="portal-property-label">{propertyName}</div>
          <div className="portal-change-title">Set your password</div>
          <div className="portal-change-subtitle">
            For security, change your temporary password before continuing.
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="portal-error">{error}</div>}

          <label className="portal-label">
            <span className="portal-label-text">Current (temporary) password</span>
            <input
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="portal-input"
            />
          </label>

          <label className="portal-label">
            <span className="portal-label-text">New password</span>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              className="portal-input"
            />
            <div className="portal-hint">At least 8 characters.</div>
          </label>

          <label className="portal-label">
            <span className="portal-label-text">Confirm new password</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
              className="portal-input"
            />
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="portal-button"
          >
            {submitting ? 'Saving…' : 'Save new password'}
          </button>
        </form>

        <button
          type="button"
          onClick={() => logout()}
          className="portal-button-ghost"
        >
          Sign out instead
        </button>
      </div>
    </div>
  );
}
